import {useEffect, useState} from 'react';
import {createContext, useContext, useReducer, ReactNode} from 'react';
import {
    Product,
    ProductVariation,
    CartItem,
    ShoppingState,
    ShoppingAction,
    CalendarCustomization,
    ItemPersonalization,
    BusinessCardCustomization,
    Department as ProductDepartment
} from '../types';
import {useCart} from '../hooks/useCart';
import {useUser} from './UserContext';
import {CartService} from '../services/cartService';
import {DepartmentService} from '../services/departmentService';

const createInitialState = (products: Product[] = [], departments: ProductDepartment[] = []): ShoppingState => ({
    products,
    departments,
    cart: [],
    isCartOpen: false,
    quickViewProduct: null,
    isQuickViewOpen: false,
    toasts: [],
});

// Helper function to get cart item identifier
// Use server ID if available, otherwise construct from productId and variationId
function getCartItemId(item: CartItem): string {
    return item.id || `${item.productId}-${item.variationId}`;
}

// Reducer helper functions
function addToCart(state: ShoppingState, action: {
    product: Product;
    variation: ProductVariation;
    quantity: number;
    calendarCustomization?: CalendarCustomization;
    personalizations?: ItemPersonalization[];
    businessCardCustomization?: BusinessCardCustomization;
    customizeItemCustomization?: BusinessCardCustomization;
}): ShoppingState {
    const {product, variation, quantity, calendarCustomization, personalizations, businessCardCustomization, customizeItemCustomization} = action;
    const existingItemIndex = state.cart.findIndex(
        item => item.productId === product.id && item.variationId === String(variation.id) && !item.calendarCustomization && !item.personalizations?.length && !item.businessCardCustomization && !item.customizeItemCustomization
    );

    if (existingItemIndex >= 0 && !calendarCustomization && !personalizations?.length && !businessCardCustomization && !customizeItemCustomization) {
        const updatedCart = [...state.cart];
        updatedCart[existingItemIndex].quantity += quantity;
        return {...state, cart: updatedCart};
    } else {
        const newCartItem: CartItem = {
            productId: product.id,
            variationId: String(variation.id),
            quantity,
            product,
            variation,
            calendarCustomization,
            personalizations,
            businessCardCustomization,
            customizeItemCustomization,
        };
        return {...state, cart: [...state.cart, newCartItem]};
    }
}

function updatePersonalization(state: ShoppingState, action: {
    cartItemId: string;
    personalizations: ItemPersonalization[];
}): ShoppingState {
    const {cartItemId, personalizations} = action;
    return {
        ...state,
        cart: state.cart.map(item =>
            getCartItemId(item) === cartItemId
                ? {...item, personalizations}
                : item
        ),
    };
}

function removeFromCart(state: ShoppingState, cartItemId: string): ShoppingState {
    return {
        ...state,
        cart: state.cart.filter(item => getCartItemId(item) !== cartItemId),
    };
}

function updateQuantity(state: ShoppingState, action: { cartItemId: string; quantity: number }): ShoppingState {
    const {cartItemId, quantity} = action;
    if (quantity <= 0) {
        return {
            ...state,
            cart: state.cart.filter(item => getCartItemId(item) !== cartItemId),
        };
    }
    return {
        ...state,
        cart: state.cart.map(item =>
            getCartItemId(item) === cartItemId
                ? {...item, quantity}
                : item
        ),
    };
}

function updateCalendarCustomization(state: ShoppingState, action: {
    cartItemId: string;
    calendarCustomization: CalendarCustomization
}): ShoppingState {
    const {cartItemId, calendarCustomization} = action;
    return {
        ...state,
        cart: state.cart.map(item =>
            getCartItemId(item) === cartItemId
                ? {...item, calendarCustomization}
                : item
        ),
    };
}

function updateBusinessCardCustomization(state: ShoppingState, action: {
    cartItemId: string;
    businessCardCustomization: BusinessCardCustomization
}): ShoppingState {

    const {cartItemId, businessCardCustomization} = action;
    return {
        ...state,
        cart: state.cart.map(item =>
            getCartItemId(item) === cartItemId
                ? {...item, businessCardCustomization}
                : item
        ),
    };
}

function updateCustomizeItemCustomization(state: ShoppingState, action: {
    cartItemId: string;
    customizeItemCustomization: BusinessCardCustomization
}): ShoppingState {
    const {cartItemId, customizeItemCustomization} = action;
    return {
        ...state,
        cart: state.cart.map(item =>
            getCartItemId(item) === cartItemId
                ? {...item, customizeItemCustomization}
                : item
        ),
    };
}

function shoppingReducer(state: ShoppingState, action: ShoppingAction): ShoppingState {
    switch (action.type) {
        case 'SET_PRODUCTS':
            return {...state, products: action.payload};
        case 'SET_DEPARTMENTS':
            return {...state, departments: action.payload};
        case 'SET_CART':
            return {...state, cart: action.payload};
        case 'ADD_TO_CART':
            return addToCart(state, action.payload);
        case 'REMOVE_FROM_CART':
            return removeFromCart(state, action.payload);
        case 'UPDATE_QUANTITY':
            return updateQuantity(state, action.payload);
        case 'UPDATE_CALENDAR_CUSTOMIZATION':
            return updateCalendarCustomization(state, action.payload);
        case 'UPDATE_BUSINESS_CARD_CUSTOMIZATION':
            return updateBusinessCardCustomization(state, action.payload);
        case 'UPDATE_CUSTOMIZE_ITEM_CUSTOMIZATION':
            return updateCustomizeItemCustomization(state, action.payload);
        case 'UPDATE_PERSONALIZATION':
            return updatePersonalization(state, action.payload);
        case 'TOGGLE_CART':
            return {...state, isCartOpen: !state.isCartOpen};
        case 'SET_CART_OPEN':
            return {...state, isCartOpen: action.payload};
        case 'OPEN_QUICK_VIEW':
            return {...state, quickViewProduct: action.payload, isQuickViewOpen: true};
        case 'CLOSE_QUICK_VIEW':
            return {...state, quickViewProduct: null, isQuickViewOpen: false};
        case 'SHOW_TOAST':
            return {...state, toasts: [...state.toasts, action.payload]};
        case 'REMOVE_TOAST':
            return {...state, toasts: state.toasts.filter(toast => toast.id !== action.payload)};
        case 'CLEAR_CART':
            return {
                ...state,
                cart: []
            };
        default:
            return state;
    }
}

const ShoppingContext = createContext<{
    state: ShoppingState;
    dispatch: (action: ShoppingAction) => Promise<void> | void;
    loading: boolean;
    cartLoading: boolean;
    cartInitialized: boolean;
    departmentsLoading: boolean;
    productsLoading: boolean;
    setProductsLoading: (v: boolean) => void;
    error: string | null;
    currentPage: number;
    totalPages: number;
    total: number;
} | null>(null);

export function ShoppingProvider({
                                     children,
                                     initialProducts
                                 }: {
    children: ReactNode;
    initialProducts?: Product[];
}) {
    const {loading: userLoading} = useUser();
    const [departmentsLoading, setDepartmentsLoading] = useState(true);
    const [productsLoading, setProductsLoading] = useState(true);
    const [state, rawDispatch] = useReducer(shoppingReducer, createInitialState(initialProducts ?? []));

    // Load departments after auth check completes
    useEffect(() => {
        if (userLoading) {
            return;
        }

        let isMounted = true;
        const loadDepartments = async () => {
            try {
                const fetchedDepartments = await DepartmentService.getDepartments();
                if (isMounted) {
                    // Dispatch directly so state.departments and departmentsLoading update in the same render
                    rawDispatch({type: 'SET_DEPARTMENTS', payload: fetchedDepartments});
                    setDepartmentsLoading(false);
                }
            } catch (error) {
                console.error('Failed to load departments:', error);
                if (isMounted) {
                    setDepartmentsLoading(false);
                }
            }
        };
        loadDepartments();

        // Listen for department updates (triggered by product search)
        const handleDepartmentsUpdated = (event: Event) => {
            const customEvent = event as CustomEvent;
            if (isMounted && customEvent.detail !== undefined) {
                rawDispatch({type: 'SET_DEPARTMENTS', payload: customEvent.detail});
            }
        };

        window.addEventListener('departmentsUpdated', handleDepartmentsUpdated);

        return () => {
            isMounted = false;
            window.removeEventListener('departmentsUpdated', handleDepartmentsUpdated);
        };
    }, [userLoading]);

    // Load cart from server (server determines cart ID based on user authentication)
    const {cartItems, loading: cartLoading, initialized: cartFetchDone} = useCart(!userLoading);
    const [cartInitialized, setCartInitialized] = useState(false);

    // Enhanced dispatch that syncs with server
    const dispatch = (action: ShoppingAction): Promise<void> | void => {
        // For actions that don't need server sync, handle synchronously
        switch (action.type) {
            case 'SET_PRODUCTS':
            case 'SET_DEPARTMENTS':
            case 'SET_CART':
            case 'TOGGLE_CART':
            case 'SET_CART_OPEN':
            case 'OPEN_QUICK_VIEW':
            case 'CLOSE_QUICK_VIEW':
            case 'SHOW_TOAST':
            case 'REMOVE_TOAST':
            case 'UPDATE_PERSONALIZATION':
                rawDispatch(action);
                return;
        }

        // For actions that need server sync, handle asynchronously
        // Dispatch to local state first
        rawDispatch(action);

        // Return the async operation for actions that need server sync
        return (async () => {
            try {
                switch (action.type) {
                    case 'ADD_TO_CART': {
                        const result = await CartService.addToCart(
                            action.payload.product.id,
                            action.payload.variation.id.toString(),
                            action.payload.quantity,
                            action.payload.calendarCustomization,
                            action.payload.variation.color,
                            action.payload.variation.size,
                            action.payload.personalizations,
                            action.payload.businessCardCustomization,
                            action.payload.logoOption?.id ?? null,
                            action.payload.secondLogoOption?.id ?? null,
                            action.payload.customizeItemCustomization,
                        );

                        if (result.success) {
                            rawDispatch({
                                type: 'SHOW_TOAST',
                                payload: {
                                    id: `${Date.now()}-${action.payload.product.id}`,
                                    message: 'Added to cart',
                                    productName: action.payload.product.name,
                                    productImage: action.payload.product.images[0],
                                    type: 'success',
                                },
                            });
                        } else if (result.warningMessage) {
                            rawDispatch({
                                type: 'SHOW_TOAST',
                                payload: {
                                    id: `${Date.now()}-warning`,
                                    message: result.warningMessage,
                                    productName: action.payload.product.name,
                                    productImage: action.payload.product.images[0],
                                    type: 'error',
                                },
                            });
                        }

                        // Refetch cart from server to synchronize state
                        const updatedCart = await CartService.getCart();
                        rawDispatch({type: 'SET_CART', payload: updatedCart});
                        break;
                    }

                    case 'UPDATE_QUANTITY': {
                        const {cartItemId, quantity} = action.payload;
                        // Use the cart item ID from the payload
                        const result = await CartService.updateCartItemQuantity(cartItemId, quantity);

                        // Show warning toast if there was a validation error
                        if (!result.success && result.warningMessage) {
                            rawDispatch({
                                type: 'SHOW_TOAST',
                                payload: {
                                    id: `${Date.now()}-warning`,
                                    message: result.warningMessage,
                                    productName: '', // Product info not available in this action
                                    productImage: '',
                                    type: 'error',
                                },
                            });
                        }

                        // Refetch cart from server to synchronize state
                        const updatedCartAfterUpdate = await CartService.getCart();
                        rawDispatch({type: 'SET_CART', payload: updatedCartAfterUpdate});
                        break;
                    }

                    case 'UPDATE_CALENDAR_CUSTOMIZATION': {
                        const {cartItemId, calendarCustomization} = action.payload;
                        const result = await CartService.updateCartItemCustomization(cartItemId, calendarCustomization);

                        if (!result.success && result.warningMessage) {
                            rawDispatch({
                                type: 'SHOW_TOAST',
                                payload: {
                                    id: `${Date.now()}-warning`,
                                    message: result.warningMessage,
                                    productName: '',
                                    productImage: '',
                                    type: 'error',
                                },
                            });
                        }

                        const updatedCartAfterCalendarCustomization = await CartService.getCart();
                        rawDispatch({type: 'SET_CART', payload: updatedCartAfterCalendarCustomization});
                        break;
                    }

                    case 'UPDATE_BUSINESS_CARD_CUSTOMIZATION': {
                        const {cartItemId, businessCardCustomization} = action.payload;
                        const result = await CartService.updateCartItemCustomization(cartItemId, undefined, businessCardCustomization);

                        if (!result.success && result.warningMessage) {
                            rawDispatch({
                                type: 'SHOW_TOAST',
                                payload: {
                                    id: `${Date.now()}-warning`,
                                    message: result.warningMessage,
                                    productName: '',
                                    productImage: '',
                                    type: 'error',
                                },
                            });
                        }

                        const updatedCartAfterBusinessCardCustomization = await CartService.getCart();
                        rawDispatch({type: 'SET_CART', payload: updatedCartAfterBusinessCardCustomization});
                        break;
                    }

                    case 'UPDATE_CUSTOMIZE_ITEM_CUSTOMIZATION': {
                        const {cartItemId, customizeItemCustomization} = action.payload;
                        const result = await CartService.updateCartItemCustomization(cartItemId, undefined, undefined, customizeItemCustomization);

                        if (!result.success && result.warningMessage) {
                            rawDispatch({
                                type: 'SHOW_TOAST',
                                payload: {
                                    id: `${Date.now()}-warning`,
                                    message: result.warningMessage,
                                    productName: '',
                                    productImage: '',
                                    type: 'error',
                                },
                            });
                        }

                        const updatedCartAfterCustomizeItemCustomization = await CartService.getCart();
                        rawDispatch({type: 'SET_CART', payload: updatedCartAfterCustomizeItemCustomization});
                        break;
                    }

                    case 'REMOVE_FROM_CART': {
                        const result = await CartService.removeFromCart(action.payload);

                        // Show warning toast if there was a validation error
                        if (!result.success && result.warningMessage) {
                            rawDispatch({
                                type: 'SHOW_TOAST',
                                payload: {
                                    id: `${Date.now()}-warning`,
                                    message: result.warningMessage,
                                    productName: '', // Product info not available in this action
                                    productImage: '',
                                    type: 'error',
                                },
                            });
                        }

                        // Refetch cart from server to synchronize state
                        const updatedCartAfterRemove = await CartService.getCart();
                        rawDispatch({type: 'SET_CART', payload: updatedCartAfterRemove});
                        break;
                    }

                    case 'CLEAR_CART': {
                        await CartService.clearCart();
                        rawDispatch({type: 'SET_CART', payload: []});
                        break;
                    }
                }
            } catch (error) {
                console.error('Failed to sync cart with server:', error);
                // Continue even if server sync fails - local state is already updated
            }
        })();
    };



    // Update cart in state when cart items are loaded from server
    useEffect(() => {
        if (cartFetchDone) {
            dispatch({type: 'SET_CART', payload: cartItems});
            setCartInitialized(true);
        }
    }, [cartItems, cartFetchDone]);

    return (
        <ShoppingContext.Provider value={{
            state,
            dispatch,
            loading: cartLoading || departmentsLoading,
            cartLoading,
            cartInitialized,
            departmentsLoading,
            productsLoading,
            setProductsLoading,
            error: null,
            currentPage: 1,
            totalPages: 1,
            total: 0,
        }}>
            {children}
        </ShoppingContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useShopping() {
    const context = useContext(ShoppingContext);
    if (!context) {
        throw new Error('useShopping must be used within a ShoppingProvider');
    }
    return context;
}
